/**
 * Team Structure Types
 *
 * Defines supervisor-staff relationships and team assignments.
 * Enables supervisors to manage their team members.
 */

/**
 * Assignment of a staff member to a supervisor
 */
export interface TeamAssignment {
  id: number;
  organization_id: number;
  supervisor_id: number;
  supervisor_name?: string;
  supervisor_email?: string;
  staff_id: number;
  staff_name?: string;
  staff_email?: string;
  staff_role?: string;
  assignment_type: TeamAssignmentType;
  effective_from: string;
  effective_to?: string | null;
  notes?: string;
  created_by: number;
  created_by_name?: string;
  created_at: string;
  updated_at?: string;
}

/**
 * Types of team assignments
 */
export type TeamAssignmentType = 'direct' | 'temporary' | 'backup';

/**
 * Create/Update payload for team assignment
 */
export interface TeamAssignmentCreate {
  supervisor_id: number;
  staff_id: number;
  assignment_type: TeamAssignmentType;
  effective_from?: string;
  effective_to?: string | null;
  notes?: string;
}

/**
 * Bulk assignment payload
 */
export interface BulkTeamAssignment {
  supervisor_id: number;
  staff_ids: number[];
  assignment_type: TeamAssignmentType;
  effective_from?: string;
  notes?: string;
}

/**
 * Transfer staff to different supervisor
 */
export interface TeamTransfer {
  staff_ids: number[];
  from_supervisor_id: number;
  to_supervisor_id: number;
  effective_from?: string;
  notes?: string;
}

/**
 * Team member with aggregated stats (for supervisor view)
 * Matches backend TeamMemberStats from team_management.py
 */
export interface TeamMemberWithStats {
  user_id: number;
  user_name: string;
  user_email?: string;
  role_type?: string;
  is_active: boolean;
  // Goal metrics
  goals_total: number;
  goals_completed: number;
  goals_on_track: number;
  goals_at_risk: number;
  // Task metrics
  pending_tasks: number;
  overdue_tasks: number;
  total_tasks: number;
  completed_tasks: number;
  // Other metrics
  enrollments_today: number;
  compliance_score: number;
}

/**
 * Supervisor with their team summary
 */
export interface SupervisorTeamSummary {
  supervisor_id: number;
  supervisor_name: string;
  supervisor_email: string;
  team_size: number;
  direct_reports: number;
  temporary_assignments: number;
  backup_assignments: number;
  team_members: TeamMemberWithStats[];
}

/**
 * Organization team overview
 */
export interface OrganizationTeamOverview {
  organization_id: number;
  total_staff: number;
  total_supervisors: number;
  unassigned_staff: number;
  assignments_by_type: {
    direct: number;
    temporary: number;
    backup: number;
  };
  supervisors: SupervisorTeamSummary[];
}

/**
 * Assignment type labels
 */
export const ASSIGNMENT_TYPE_LABELS: Record<TeamAssignmentType, { en: string; es: string }> = {
  direct: { en: 'Direct Report', es: 'Reporte Directo' },
  temporary: { en: 'Temporary Assignment', es: 'Asignación Temporal' },
  backup: { en: 'Backup Coverage', es: 'Cobertura de Respaldo' },
};

/**
 * Get assignment type label
 */
export function getAssignmentTypeLabel(type: TeamAssignmentType, language: 'en' | 'es'): string {
  return ASSIGNMENT_TYPE_LABELS[type][language];
}
